// MultiStepForm.js
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import {
  Container,
  Form,
  Button,
  ProgressBar,
  Nav,
  Alert,
} from "react-bootstrap";
import SignaturePad from "react-signature-canvas"; // Import the SignaturePad component
import "./Signature.css";
import { getSasToken } from "../../store/thunk/user";
import { connect } from "react-redux";
import { updateForm } from "../../utils/updateForm";
import { dataURLtoBlob } from "../../utils/dataURLtoBlob";
import { uploadDocumentAndGetUrl } from "../../utils/uploadDocument";
import { triggerButtonClick } from "../../utils/triggerLibraryButton";
import { toast } from "react-toastify";
import HealthQuestionnaireDownload from "./Download/HealthQuestionnaireDownload";
import { Tabs, Tab, Box } from "@mui/material";

export const healthOptions = [
  "Diabetes",
  "Heart or circulatory disorders",
  "Stomach or intestinal disorders",
  "Any condition which causes difficulties sleeping",
  "Chronic chest disorders (especially if night-time symptoms are troublesome)",
  "Any medical condition requiring medication to a strict timetable",
  "Any other health factors that might affect fitness at work",
];

const HealthQuestionnaire = ({
  currentDocument,
  documentList,
  user,
  sasToken,
  ipAddress,
  getSasToken,
}) => {
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(0); // Progress percentage for each step
  const [value, setValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    surName: "",
    homeAddress: "",
    postCode: "",
    mobile: "",
    landlineNumber: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    jobTitle: "",
    nationalInsuranceNumber: "",
    healthConditions: [],
    healthConditionConsent: "",
    healthConditionConsentAgree: "",
    otherOtherCondition: "",
    consent: false,
    signature: "", // Store the signature data as a string
  });
  const [errors, setErrors] = useState({});
  const sigPadRef = useRef(null); // Reference to the SignaturePad component
  // Function to handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  useEffect(() => {
    setFormData(currentDocument);
    if (currentDocument?.step) {
      setStep(currentDocument?.step);
    }
    if (currentDocument?.progress) {
      setProgress(currentDocument?.progress);
    }
    if (currentDocument?.progress === 100) {
      getSasToken();
    }
  }, []);

  // Function to validate each step
  const validateStep = () => {
    let formErrors = {};
    if (step === 1) {
      if (!formData.title) formErrors.title = "Title is required";
      if (!formData.firstName) formErrors.firstName = "First Name is required";
      if (!formData.surName) formErrors.surName = "Surname is required";
      if (!formData.homeAddress)
        formErrors.homeAddress = "Home address is required";
      if (!formData.postCode) formErrors.postCode = "Post code is required";
      if (!formData.mobile) formErrors.mobile = "Mobile number is required";
      if (!/^\d{11}$/.test(formData.mobile)) {
        formErrors.mobile = "Mobile number must be exactly 11 digits and contain only numbers";
      }
      if (!formData.email) {
        formErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        formErrors.email = "Email is invalid";
      }
    } else if (step === 2) {
    } else if (step === 3) {
      if (!formData.consent) formErrors.consent = "You must agree to the terms";
      if (sigPadRef.current.isEmpty())
        formErrors.signature = "Signature is required"; // Check if the signature pad is empty
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Function to go to the next step
  const nextStep = () => {
    if (validateStep()) {
      const payload = {
        [currentDocument["key"]]: {
          name: currentDocument?.name,
          ...formData,
          progress: progress + 33,
          step: step + 1,
        },
      };
      updateForm(payload, documentList, user);
      setStep(step + 1);
      setProgress(progress + 33);
    }
  };

  // Function to go to the previous step
  const prevStep = () => {
    setStep(step - 1);
    setProgress(progress - 33);
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      // Get the signature data as a base64 image
      const signatureImage = sigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");

      const signatureBlob = dataURLtoBlob(signatureImage);

      setFormData({ ...formData, signature: signatureImage });

      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob,
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            progress: 100,
            step: step,
            signature: documentUrl,
            signDate: new Date(),
            ipAddress: ipAddress,
          },
        };
        updateForm(payload, documentList, user);
        toast.success("Successfully filled the form.");
        triggerButtonClick();
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
      // Additional submit logic here, such as sending formData to the server
    }
  };
  // Clear the signature pad
  const clearSignature = () => {
    sigPadRef.current.clear();
    setFormData({ ...formData, signature: "" });
  };

  // Handle checkbox changes
  const handleCheckChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => {
      const healthConditions = prevData?.healthConditions || []; // Default to an empty array
      if (checked) {
        return {
          ...prevData,
          healthConditions: [...healthConditions, name],
        };
      } else {
        return {
          ...prevData,
          healthConditions: healthConditions.filter(
            (condition) => condition !== name
          ),
        };
      }
    });
  };

  return (
    <Container className="mt-5">
      {currentDocument?.progress === 100 ? (
        <>
          <HealthQuestionnaireDownload
            data={currentDocument}
            sasToken={sasToken}
            user={documentList?.userDetail}
          />
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="basic tabs example"
              className="tab mt-2"
            >
              <Tab label="Personal Information" />
              <Tab label="Health conditions" />
            </Tabs>
            {value === 0 && (
              <div className="container">
                <div className="row">
                  <h4 className="mt-2">Personal Information</h4>
                  <h2>
                    Health questionnaire to assess if you are fit to work nights
                  </h2>
                  <p>
                    <small>
                      The purpose of this questionnaire is to make sure that you
                      are suited to working at night. All the information you
                      provide will be kept confidential.
                    </small>
                  </p>
                  <ul class="list-group">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Title</strong>{" "}
                      <span>{currentDocument?.title || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>First Name</strong>{" "}
                      <span>{currentDocument?.firstName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Surname</strong>{" "}
                      <span>{currentDocument?.surName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Home Address</strong>{" "}
                      <span>{currentDocument?.homeAddress || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Postcode</strong>{" "}
                      <span>{currentDocument?.postCode || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Mobile Number</strong>{" "}
                      <span>{currentDocument?.mobile || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Landline Number</strong>{" "}
                      <span>{currentDocument?.landlineNumber || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Email Address</strong>{" "}
                      <span>{currentDocument?.email || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>DOB</strong>{" "}
                      <span>{currentDocument?.dateOfBirth || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Job Title</strong>{" "}
                      <span>{currentDocument?.jobTitle || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>National Insurance Number</strong>{" "}
                      <span>
                        {currentDocument?.nationalInsuranceNumber || "--"}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
            {value === 1 && (
              <Box sx={{ p: 3 }}>
                <h2 className="mt-2">Health conditions</h2>
                <ul class="list-group mt-2 pad-0">
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      Do you suffer from any of the following health conditions?
                    </strong>{" "}
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>Diabetes</strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes("Diabetes")
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>Heart or circulatory disorders</strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes(
                        "Heart or circulatory disorders"
                      )
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>Stomach or intestinal disorders</strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes(
                        "Stomach or intestinal disorders"
                      )
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      Any condition which causes difficulties sleeping
                    </strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes(
                        "Any condition which causes difficulties sleeping"
                      )
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      Chronic chest disorders (especially if night-time symptoms
                      are troublesome)
                    </strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes(
                        "Chronic chest disorders (especially if night-time symptoms are troublesome)"
                      )
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      Any medical condition requiring medication to a strict
                      timetable
                    </strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes(
                        "Any medical condition requiring medication to a strict timetable)"
                      )
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      Any other health factors that might affect fitness at work
                    </strong>{" "}
                    <span>
                      {currentDocument?.healthConditions?.includes(
                        "Any other health factors that might affect fitness at work"
                      )
                        ? "Yes"
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>Other Factor?</strong>{" "}
                    <span>
                      {currentDocument?.otherOtherCondition
                        ? String(currentDocument?.otherOtherCondition)
                        : "Not answered"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      If you have answered 'yes' to any of the above questions,
                      you may be asked to see a doctor or nurse
                    </strong>{" "}
                    <span>
                      {currentDocument?.healthConditionConsent || "--"}
                    </span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between">
                    <strong>
                      I, the undersigned, confirm that the above is correct to
                      the best of knowledge
                    </strong>{" "}
                    <span>
                      {currentDocument?.healthConditionConsentAgree || "Disagree"}
                    </span>
                  </li>
                </ul>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          {" "}
          <Nav variant="pills" className="justify-content-center mb-3">
            <Nav.Item>
              <Nav.Link
                className={step === 1 ? "text-light" : ""}
                active={step === 1}
              >
                Step 1
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 2 ? "text-light" : ""}
                active={step === 2}
              >
                Step 2
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 3 ? "text-light" : ""}
                active={step === 3}
              >
                Step 3
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <ProgressBar now={progress} className="mb-4" />
          <Form onSubmit={handleSubmit}>
            {step === 1 && (
              <div>
                <h3>Section 1: Personal Information</h3>
                <h2>
                  Health questionnaire to assess if you are fit to work nights
                </h2>
                <p>
                  <small>
                    The purpose of this questionnaire is to make sure that you
                    are suited to working at night. All the information you
                    provide will be kept confidential.
                  </small>
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    required
                  />
                  {errors.title && (
                    <Alert variant="danger">{errors.title}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    required
                  />
                  {errors.firstName && (
                    <Alert variant="danger">{errors.firstName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Surname</Form.Label>
                  <Form.Control
                    type="text"
                    name="surName"
                    value={formData.surName}
                    onChange={handleChange}
                    isInvalid={!!errors.surName}
                    required
                  />
                  {errors.surName && (
                    <Alert variant="danger">{errors.surName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Home Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="homeAddress"
                    value={formData.homeAddress}
                    onChange={handleChange}
                    isInvalid={!!errors.homeAddress}
                    required
                  />
                  {errors.homeAddress && (
                    <Alert variant="danger">{errors.homeAddress}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Postcode</Form.Label>
                  <Form.Control
                    type="text"
                    name="postCode"
                    value={formData.postCode}
                    onChange={handleChange}
                    isInvalid={!!errors.postCode}
                    required
                  />
                  {errors.postCode && (
                    <Alert variant="danger">{errors.postCode}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    isInvalid={!!errors.mobile}
                    required
                  />
                  {errors.mobile && (
                    <Alert variant="danger">{errors.mobile}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Landline Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="landlineNumber"
                    value={formData.landlineNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    required
                  />
                  {errors.email && (
                    <Alert variant="danger">{errors.email}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Date Of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Job Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>National Insurance Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="nationalInsuranceNumber"
                    value={formData.nationalInsuranceNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Step 2: Health conditions</h3>
                <p className="mt-4">
                  Do you suffer from any of the following health conditions?{" "}
                </p>
                {healthOptions.map((option, index) => (
                  <Form.Group className="mb-3" key={index}>
                    <Form.Check
                      type="checkbox"
                      name={option}
                      label={option}
                      checked={formData?.healthConditions?.includes(option)}
                      onChange={handleCheckChange}
                    />
                  </Form.Group>
                ))}
                <Form.Group className="mb-3">
                  <Form.Label>Other Factor</Form.Label>
                  <Form.Control
                    type="text"
                    name="otherOtherCondition"
                    value={formData.otherOtherCondition}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    If you have answered 'yes' to any of the above questions,
                    you may be asked to see a doctor or nurse
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    name="healthConditionConsent"
                    label="Yes"
                    value="Yes"
                    checked={formData.healthConditionConsent === "Yes"}
                    onChange={handleChange}
                  />
                  <Form.Check
                    type="radio"
                    name="healthConditionConsent"
                    label="No"
                    value="No"
                    checked={formData.healthConditionConsent === "No"}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    I, the undersigned, confirm that the above is correct to the
                    best of knowledge
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    name="healthConditionConsentAgree"
                    label="Agree"
                    value="Agree"
                    checked={formData.healthConditionConsentAgree === "Agree"}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3>Step 3: Sign and Submit</h3>
                <p>
                  By signing this page, I confirm that I fully understand the
                  content and agree to adhere to it at all times. I also agree
                  that all the details provided in this document are accurate
                  and complete to the best of my knowledge.
                </p>
                {/* Signature Pad Section */}
                <Form.Group className="mb-3">
                  <Form.Label>Signature</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={sigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.signature && (
                    <Alert variant="danger">{errors.signature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="consent"
                    label="Clear By ticking this box I consent to the company processing my
                data and the evidence of this document being stored within their
                system for the purpose of work finding services."
                    checked={formData.consent} // Use checked for checkboxes
                    onChange={handleChange}
                    isInvalid={!!errors.consent}
                  />
                  {errors.consent && (
                    <Alert variant="danger">{errors.consent}</Alert>
                  )}
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="success" type="submit">
                  Submit
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currentDocument: state.user.currentDocument,
  documentList: state.user.documentList,
  user: state.user.loggedInUserData,
  sasToken: state.user.sasToken,
  ipAddress: state.user.ipAddress,
});

export default connect(mapStateToProps, { getSasToken })(HealthQuestionnaire);
